<template>
  <TWModal
    :title="$t('envelope.reminderSetModal.setReminders')"
    :show="show"
    :loading="loading"
    @close="$emit('close')"
  >
    <template v-slot:content>
      <div v-if="nextRemind && remindDays">
        <div class="flex items-center">
          <InfoIndicator
            class="mr-sm"
            size="small"
          />
          <p>
            <span>
              {{ $t('envelope.messageRemindersOccur.a') }},
              {{ $tc('envelope.messageRemindersOccur.b', remindDays, { remindDays }) }}
              {{ $t('envelope.messageRemindersOccur.c') }}
              {{ nextRemind | formatDate('calendar') }}.
            </span>
          </p>
        </div>
        <hr />
      </div>
      <p>{{ $t('envelope.reminderSetModal.setRemindersToOccur') }}</p>
      <div class="grid grid-cols-2 gap-base">
        <div
          v-for="item in reminderIntervals"
          :key="item.value"
          class="flex items-center"
        >
          <VCheckbox
            type="radio"
            :ariaLabel="$t('envelope.reminderSetModal.checkboxAria', { x: item.value })"
            :id="`reminder.${item.value}`"
            name="reminder-interval"
            data-ec="set_reminders_modal"
            :data-et="`${item.label}_reminder_option`"
            :checked="defaultOption === item.value"
            v-model="reminders.radio"
            :radio-value="item.value"
          />
          <label
            :for="`reminder.${item.value}`"
            class="cursor-pointer pl-base"
          >
            {{ item.label }}
          </label>
        </div>
      </div>
      <HeightTransition>
        <FloatingLabelInput
          v-if="reminders.radio === 'custom'"
          class="mt-base"
          type="number"
          :min="0"
          :step="1"
          :value="customReminder"
          @input="updateCustomReminder"
          :fieldIsRequired="false"
          :placeholder="$t('envelope.reminderSetModal.setCustomInterval')"
          :aria-label="$t('envelope.reminderSetModal.setCustomInterval')"
          data-test="reminders-custom_interval"
          data-hj-suppress
          data-ec="bulk_send_reminders_page"
          data-et="custom_interval_input"
        />
      </HeightTransition>
    </template>

    <template v-slot:controls>
      <div class="w-full flex flex-col justify-between gap-lg sm:flex-row">
        <BaseButton
          variant="danger"
          @click.native="handleOpenCancelRemindersModal"
          data-ec="set_reminders_modal"
          data-et="cancel_reminders_button"
          v-if="nextRemind || remindDays"
        >
          {{ $t('envelope.reminderSetModal.cancelReminders') }}
        </BaseButton>

        <div class="flex flex-col-reverse gap-base sm:block">
          <BaseButton
            variant="invisible"
            @click.native="$emit('close')"
            data-ec="set_reminders_modal"
            data-et="cancel_button"
          >
            {{ $t('functional.cancel') }}
          </BaseButton>

          <BaseButton
            @click.native="handleSetReminders"
            data-ec="set_reminders_modal"
            data-et="set_reminders_button"
          >
            {{ $t('envelope.reminderSetModal.setReminders') }}
          </BaseButton>
        </div>
      </div>
    </template>
  </TWModal>
</template>

<script>
import VCheckbox from '@/components/deprecated/atoms/Checkbox.vue';
import TWModal from '@/components/tailwind/Modal.vue';
import BaseButton from '@/components/library/buttons/BaseButton.vue';
import InfoIndicator from '@/components/library/indicators/InfoIndicator.vue';
import FloatingLabelInput from '@/components/tailwind/forms/FloatingLabelInput.vue';
import HeightTransition from '@/transitions/HeightTransition.vue';

import { logAmplitudeEvent } from '@/utils/analytics';

export default {
  name: 'RemindersSetModal',
  props: {
    envelope: {
      type: [Object],
      required: true,
    },
    show: {
      type: Boolean,
      default: false,
    },
    setReminders: {
      type: [Object],
      required: true,
    },
    // TODO: remove when envelope module is fixed
    // and comes from one data source
    creating: {
      type: [Boolean],
      default: false,
    },
  },
  components: {
    VCheckbox,
    HeightTransition,
    FloatingLabelInput,
    TWModal,
    BaseButton,
    InfoIndicator,
  },
  data() {
    return {
      reminderIntervals: [
        {
          label: this.$t('envelope.reminderSetModal.period.everyday'),
          value: '1',
        },
        {
          label: this.$t('envelope.reminderSetModal.period.twoDays'),
          value: '2',
        },
        {
          label: this.$t('envelope.reminderSetModal.period.threeDays'),
          value: '3',
        },
        {
          label: this.$t('envelope.reminderSetModal.period.fiveDays'),
          value: '5',
        },
        {
          label: this.$t('envelope.reminderSetModal.period.sevenDays'),
          value: '7',
        },
        {
          label: this.$t('envelope.reminderSetModal.period.custom'),
          value: 'custom',
        },
      ],
      loading: false,
      reminders: {
        loading: false,
        radio: null,
        custom: null,
        days: null,
      },
    };
  },
  computed: {
    customReminder() {
      return this.defaultOption === 'custom' ? this.remindDays : null;
    },
    defaultOption() {
      if (!this.remindDays) {
        return '1';
      }
      const isCustom = !this.reminderIntervals.map(option => option.value).includes(this.remindDays.toString());
      return isCustom ? 'custom' : this.remindDays.toString();
    },
    remindDays() {
      if (this.envelope.auto_remind_hours > 0) {
        return this.envelope.auto_remind_hours / 24;
      }
      return null;
    },
    nextRemind() {
      return this.envelope.next_remind;
    },
  },
  methods: {
    handleOpenCancelRemindersModal() {
      this.$emit('close');
      this.$emit('openCancelRemindersModal');
    },
    updateCustomReminder(value) {
      this.reminders.custom = parseInt(value, 10);
    },
    onCloseModal() {
      const scope = this.$refs.form.$el.getAttribute('data-vv-scope');
      this.errors.clear(scope);
    },
    remindButton() {
      this.loading = true;
      logAmplitudeEvent('reminderSetModal-reminder-set', {
        days: '0',
      });
      this.$store.dispatch('envelopes/SEND_ONE_OFF_REMINDER', { fingerprint: this.envelope.fingerprint })
        .then(() => {
          this.$bus.$emit('closeModal');
        }).finally(() => {
          this.loading = false;
        });
    },
    async handleSetReminders() {
      this.loading = true;
      this.reminders.fingerprint = this.envelope.fingerprint;

      if (this.reminders.radio === 'custom') {
        this.reminders.days = Math.floor(this.reminders.custom);
        if (this.reminders.days <= 0 || this.reminders.days === null) {
          this.reminders.days = 1;
        }
      } else {
        this.reminders.days = this.reminders.radio;
      }
      if (this.reminders.days === null) {
        this.reminders.days = 1;
      }

      await this.$store.dispatch('envelopes/SET_ENVELOPE_REMINDERS', { reminders: this.reminders, isCreating: this.creating }).then((response) => {
        if (response) {
          logAmplitudeEvent('reminderSetModal-reminder-set', {
            days: `${ this.reminders.days}`,
          });
          this.reminders.custom = null;
          this.$bus.$emit('renderGlobalMessage', ({
            message: this.$t('envelope.reminderSetModal.messageAutomaticRemindersSuccess'),
            icon: 'check',
            type: 'success',
          }));
          this.$emit('close');
          this.loading = false;
        }
      });
    },
  },
};
</script>
